import React, { useState } from "react";
import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const Svg = ({ children, className = "h-5 w-5", sw = 1.75 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const LockIcon = (p) => (
  <Svg {...p}>
    <rect x="4" y="11" width="16" height="10" rx="3" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </Svg>
);

export const CheckIcon = (p) => (
  <Svg sw={2.25} {...p}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Svg>
);

export const DownloadIcon = (p) => (
  <Svg {...p}>
    <path d="M12 4v11M7 11l5 5 5-5M5 20h14" />
  </Svg>
);

export const TrophyIcon = (p) => (
  <Svg sw={1.5} {...p}>
    <path d="M8 4h8v5a4 4 0 0 1-8 0V4Z" />
    <path d="M8 6H5a1 1 0 0 0-1 1 4 4 0 0 0 4 4M16 6h3a1 1 0 0 1 1 1 4 4 0 0 1-4 4" />
    <path d="M12 13v4M9 20h6M10 17h4" />
  </Svg>
);

export default function RewardLocked({
  lockedReason,
  title,
  status = "locked",
  url,
  detail,
}) {
  const unlocked = status === "unlocked";
  const [downloading, setDownloading] = useState(false);

  const handleAttachFileDownload = async (filesUrl) => {
    try {
      if (!filesUrl) return;
      const list = (Array.isArray(filesUrl) ? filesUrl : [filesUrl]).filter(Boolean);
      if (!list.length) return;

      setDownloading(true);

      const downloadPromises = [];

      list.forEach((fileUrl) => {
        const filename = fileUrl.split("?")[0].split("/").pop();
        const link = document.createElement("a");
        link.href = fileUrl;
        link.setAttribute("download", filename);
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");

        downloadPromises.push(
          new Promise((resolve) => {
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(resolve, 100);
          })
        );
      });

      await Promise.all(downloadPromises);
    } catch (error) {
      console.error("Error downloading attached file:", error);
    } finally {
      setTimeout(() => setDownloading(false), 900);
    }
  };

  const onActivate = () => unlocked && handleAttachFileDownload([url]);

  return (
    <motion.div
      variants={item}
      role={unlocked ? "button" : undefined}
      tabIndex={unlocked ? 0 : -1}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (unlocked && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onActivate();
        }
      }}
      className={`group relative h-full rounded-2xl p-[1px] outline-none transition-all duration-300 ${unlocked
          ? "cursor-pointer bg-gradient-to-br from-[#113BE1] via-[#4268FF] to-[#113BE1]/10 hover:-translate-y-1.5 focus-visible:ring-2 focus-visible:ring-[#4268FF]"
          : "cursor-not-allowed bg-borderColor"
        }`}
    >
      {unlocked && (
        <div className="pointer-events-none absolute -inset-1 -z-10 rounded-3xl bg-gradient-to-br from-[#113BE1] to-[#4268FF] opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-30" />
      )}

      <div className="relative flex h-full min-h-[220px] flex-col items-center gap-4 overflow-hidden rounded-[15px] bg-primary px-6 py-7 text-center">
        {/* Background decoration */}
        <div
          className={`pointer-events-none absolute -top-16 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full blur-3xl ${unlocked ? "bg-[#4268FF]/20" : "bg-white/5"
            }`}
        />
        {unlocked && (
          <div className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
        )}

        {/* Status pill */}
        <span
          className={`absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${unlocked
              ? "border-blue-400/40 bg-blue-500/10 text-blue-300"
              : "border-white/10 bg-white/5 text-gray-400"
            }`}
        >
          {unlocked ? (
            <>
              <CheckIcon className="h-3.5 w-3.5" /> Débloqué
            </>
          ) : (
            <>
              <LockIcon className="h-3 w-3" /> Verrouillé
            </>
          )}
        </span>

        {/* Icon badge */}
        <div
          className={`relative mt-4 flex h-16 w-16 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110 ${unlocked
              ? "bg-gradient-to-br from-[#113BE1] to-[#4268FF] text-white shadow-lg shadow-blue-500/30"
              : "border border-borderColor bg-primary text-gray-500"
            }`}
        >
          {unlocked ? (
            downloading ? (
              <CheckIcon className="h-7 w-7" />
            ) : (
              <DownloadIcon className="h-7 w-7" />
            )
          ) : (
            <LockIcon className="h-7 w-7" />
          )}
        </div>

        {/* Text */}
        <div className="flex w-full flex-1 flex-col items-center gap-1.5">
          {unlocked ? (
            <>
              <h3 className="bg-gradient-to-r from-white to-blue-200 bg-clip-text text-[16px] font-semibold leading-snug text-transparent">
                {title}
              </h3>
              {detail && (
                <p className="line-clamp-2 text-[13px] text-[#A0AEC0]">{detail}</p>
              )}
            </>
          ) : (
            <>
              <h3 className="text-[14px] font-medium leading-[150%] text-[#A0AEC0]">
                {lockedReason?.title
                  ? `Tu dois terminer "${lockedReason.title}" pour la débloquer.`
                  : "Termine l'étape requise pour la débloquer."}
              </h3>
              {title && (
                <p className="line-clamp-1 text-xs text-gray-500">{title}</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {unlocked && (
          <div className="mt-2 flex w-full items-center justify-center gap-1.5 whitespace-nowrap text-[11px] font-medium text-blue-300 transition-colors group-hover:text-blue-200">
            <DownloadIcon className="h-3.5 w-3.5 shrink-0" />
            <span>
              {downloading
                ? "Téléchargement..."
                : "Cliquez ici pour télécharger"}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}